import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { db } from '../services/firebase'
import { collection, query, where, getDocs, updateDoc, doc, limit } from 'firebase/firestore'
import { motion } from 'framer-motion'
import { 
  BellIcon, 
  CheckCircleIcon, 
  UserPlusIcon,
  CalendarIcon,
  TrashIcon,
  CheckIcon,
  XCircleIcon,
  ArrowUturnLeftIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { formatDistanceToNow } from 'date-fns'
import { resolveNotificationMessage } from '../utils/notificationMessage'

const Notifications = () => {
  const { user, userData } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')

  const getCurrentUid = () => {
    const uid = user?.uid || userData?.uid || userData?.id || null
    console.log('getCurrentUid returning:', uid, 'user:', user?.uid, 'userData:', userData?.uid)
    return uid
  }

  const getCreatedAtMillis = (value) => {
    if (!value) return 0
    if (typeof value?.toDate === 'function') return value.toDate().getTime()
    const asDate = new Date(value)
    return Number.isNaN(asDate.getTime()) ? 0 : asDate.getTime()
  }

  const resolveNotificationPath = (notification) => {
    const directPath = notification?.path || notification?.link || notification?.url
    if (typeof directPath === 'string' && directPath.trim()) {
      return directPath.trim().startsWith('/') ? directPath.trim() : `/${directPath.trim()}`
    }

    if (notification?.matchId) {
      return `/match/${notification.matchId}`
    }

    if (notification?.type === 'follow') {
      const profileUserId = notification?.senderId || notification?.actorId || notification?.fromUserId || notification?.userId
      if (profileUserId) {
        return `/profile/${profileUserId}`
      }
    }

    return '/notifications'
  }

  useEffect(() => {
    fetchNotifications()
  }, [user, userData])

  const fetchNotifications = async () => {
    const currentUid = getCurrentUid()
    if (!currentUid) {
      setNotifications([])
      setErrorMessage(t('notifications.notAuthenticated'))
      setLoading(false)
      return
    }

    try {
      setErrorMessage('')
      const loadUserNotifications = async () => {
        try {
          const [byUserId, byRecipientId, byTargetAll, byUserIds] = await Promise.all([
            getDocs(
              query(
                collection(db, 'notifications'),
                where('userId', '==', currentUid)
              )
            ),
            getDocs(
              query(
                collection(db, 'notifications'),
                where('recipientId', '==', currentUid)
              )
            ).catch(() => ({ docs: [] })),
            getDocs(
              query(
                collection(db, 'notifications'),
                where('target', '==', 'all')
              )
            ).catch(() => ({ docs: [] })),
            getDocs(
              query(
                collection(db, 'notifications'),
                where('userIds', 'array-contains', currentUid)
              )
            ).catch(() => ({ docs: [] }))
          ])

          const docMap = new Map()
          ;[byUserId, byRecipientId, byTargetAll, byUserIds].forEach((snapshot) => {
            console.log('Query result - byUserId:', byUserId.docs.length, 'byRecipientId:', byRecipientId.docs.length, 'byTargetAll:', byTargetAll.docs.length, 'byUserIds:', byUserIds.docs.length)
            snapshot.docs.forEach((notificationDoc) => {
              docMap.set(notificationDoc.id, notificationDoc)
            })
          })

          let docs = Array.from(docMap.values())
          console.log('docMap has', docs.length, 'unique docs')
          // Don't fall back to fetching all notifications - this causes security issues
          // Just return the filtered results

          return docs
        } catch (queryError) {
          console.warn('Using fallback notifications query due to index issue:', queryError)
          try {
            const snapshot = await getDocs(
              query(collection(db, 'notifications'), where('userId', '==', currentUid))
            )
            console.log('Fallback query returned:', snapshot.docs.length, 'docs')
            return snapshot.docs
          } catch (fallbackError) {
            console.error('Unable to load notifications collection:', fallbackError)
            return []
          }
        }
      }

      const loadPushNotifications = async () => {
        try {
          const [allPushSnapshot, directPushSnapshot, listPushSnapshot] = await Promise.all([
            getDocs(
              query(
                collection(db, 'pushNotifications'),
                where('target', '==', 'all')
              )
            ),
            getDocs(
              query(
                collection(db, 'pushNotifications'),
                where('userId', '==', currentUid)
              )
            ),
            getDocs(
              query(
                collection(db, 'pushNotifications'),
                where('userIds', 'array-contains', currentUid)
              )
            ).catch(() => ({ docs: [] }))
          ])

          return [
            ...allPushSnapshot.docs,
            ...directPushSnapshot.docs,
            ...listPushSnapshot.docs
          ]
        } catch (queryError) {
          if (queryError?.code === 'permission-denied') {
            console.warn('pushNotifications read denied by Firestore rules; skipping push notifications in notifications page.')
            return []
          }

          console.warn('Using fallback pushNotifications query due to index issue:', queryError)
          try {
            const fallbackPushSnapshot = await getDocs(collection(db, 'pushNotifications'))
            return fallbackPushSnapshot.docs.filter((notificationDoc) => {
              const data = notificationDoc.data()
              if (data.target === 'all') return true
              if (data.userId && data.userId === currentUid) return true
              if (Array.isArray(data.userIds) && data.userIds.includes(currentUid)) return true
              return false
            })
          } catch (fallbackError) {
            console.error('Unable to load pushNotifications collection:', fallbackError)
            return []
          }
        }
      }

      const [userNotificationDocs, pushNotificationDocs] = await Promise.all([
        loadUserNotifications(),
        loadPushNotifications()
      ])

      const userNotifications = userNotificationDocs.map((notificationDoc) => ({
        id: notificationDoc.id,
        source: 'notifications',
        ...notificationDoc.data()
      }))
      console.log('User notifications (from firestore):', userNotifications.map(n => ({ id: n.id, userId: n.userId, senderId: n.senderId, type: n.type })))

      const pushMap = new Map()
      pushNotificationDocs.forEach((notificationDoc) => {
        const data = notificationDoc.data()
        console.log('Push notification doc:', notificationDoc.id, 'userId:', data.userId, 'senderId:', data.senderId)
        const key = notificationDoc.id
        if (!pushMap.has(key)) {
          pushMap.set(key, {
            id: key,
            source: 'pushNotifications',
            read: true,
            ...data
          })
        }
      })

      let notificationsData = [...userNotifications, ...Array.from(pushMap.values())]
        .filter((item) => {
          // Exclude deleted notifications
          if (item.deleted === true) return false
          
          // EXCLUDE notifications where senderId equals currentUid - THIS IS THE KEY FIX
          const senderIdStr = String(item.senderId || '')
          const currentUidStr = String(currentUid || '')
          const shouldExclude = senderIdStr.length > 0 && senderIdStr === currentUidStr
          
          if (shouldExclude) {
            console.log('EXCLUDING notification because senderId matches current user:', item.id, 'senderId:', item.senderId)
            return false
          }
          
          // Accept if has message content OR has messageI18nKey (i18n-based notification)
          const hasMessage = typeof item.message === 'string' && item.message.trim().length > 0
          const hasI18nKey = typeof item.messageI18nKey === 'string' && item.messageI18nKey.trim().length > 0
          return hasMessage || hasI18nKey
        })
        .sort((a, b) => getCreatedAtMillis(b.createdAt) - getCreatedAtMillis(a.createdAt))

      console.log('Final notifications after filter:', notificationsData.map(n => ({ id: n.id, type: n.type, senderId: n.senderId })))

      console.log('Loaded notifications:', notificationsData.length, 'items')

      setNotifications(notificationsData)
    } catch (error) {
      console.error('Error fetching notifications:', error)
      setErrorMessage(error?.message || t('notifications.failedToFetch'))
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationId) => {
    try {
      const targetNotification = notifications.find((item) => item.id === notificationId)
      if (!targetNotification || targetNotification.source !== 'notifications') {
        return
      }

      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      })
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      )
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read && n.source === 'notifications')
      await Promise.all(
        unreadNotifications.map(notif =>
          updateDoc(doc(db, 'notifications', notif.id), { read: true })
        )
      )
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      )
    } catch (error) {
      console.error('Error marking all as read:', error)
    }
  }

  const deleteNotification = async (notificationId) => {
    try {
      const targetNotification = notifications.find((item) => item.id === notificationId)
      if (!targetNotification || targetNotification.source !== 'notifications') {
        return
      }

      await updateDoc(doc(db, 'notifications', notificationId), {
        deleted: true
      })
      setNotifications(prev => prev.filter(n => n.id !== notificationId))
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'match_invite':
        return <CalendarIcon className="h-6 w-6 text-blue-500" />
      case 'join_request':
        return <UserPlusIcon className="h-6 w-6 text-orange-500" />
      case 'invite_accepted':
      case 'player_accepted':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />
      case 'invite_declined':
      case 'player_rejected':
      case 'request_rejected':
      case 'invite_cancelled':
        return <XCircleIcon className="h-6 w-6 text-red-500" />
      case 'player_left':
        return <ArrowUturnLeftIcon className="h-6 w-6 text-yellow-500" />
      case 'player_checked_in':
        return <CheckCircleIcon className="h-6 w-6 text-blue-500" />
      case 'request_cancelled':
        return <XCircleIcon className="h-6 w-6 text-red-500" />
      case 'new_team_member':
        return <UserPlusIcon className="h-6 w-6 text-blue-500" />
      case 'follow':
        return <UserPlusIcon className="h-6 w-6 text-green-500" />
      case 'match_update':
        return <CheckCircleIcon className="h-6 w-6 text-yellow-500" />
      case 'chat_message':
        return <CalendarIcon className="h-6 w-6 text-purple-500" />
      default:
        return <BellIcon className="h-6 w-6 text-gray-500" />
    }
  }

  const handleNotificationClick = async (notification) => {
    await markAsRead(notification.id)
    
    if (notification.matchId) {
      if (notification.type === 'chat_message') {
        navigate(`/match/${notification.matchId}?chat=true`)
      } else {
        navigate(`/match/${notification.matchId}`)
      }
    } else if (notification.type === 'follow' && notification.senderId) {
      navigate(`/profile/${notification.senderId}`)
    }
  }

  const handleCloseNotifications = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate('/')
  }

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-sport-primary to-sport-secondary p-6 text-white">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">{t('notifications.title')}</h1>
              <p className="opacity-90 mt-1">{t('notifications.stayUpdated')}</p>
            </div>
            <div className="flex items-center gap-2">
              {notifications.some(n => !n.read) && (
                <button
                  onClick={markAllAsRead}
                  className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition flex items-center space-x-2"
                >
                  <CheckIcon className="h-4 w-4" />
                  <span>{t('notifications.markAllAsRead')}</span>
                </button>
              )}
              <button
                onClick={handleCloseNotifications}
                className="bg-white/20 hover:bg-white/30 px-3 py-2 rounded-lg transition flex items-center gap-1"
                aria-label="Close notifications"
              >
                <XMarkIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Close</span>
              </button>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-sport-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : errorMessage ? (
            <div className="text-center py-12 px-6">
              <p className="text-red-500 text-sm">{errorMessage}</p>
              <p className="text-gray-400 text-xs mt-2">UID: {getCurrentUid() || 'not-authenticated'}</p>
            </div>
          ) : notifications.length > 0 ? (
            notifications.map((notification, index) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition cursor-pointer border-l-4 ${
                  !notification.read 
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-l-blue-500' 
                    : 'bg-white dark:bg-gray-800 border-l-gray-300 dark:border-l-gray-700'
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm text-gray-900 dark:text-white font-medium">
                        {resolveNotificationMessage(t, notification)}
                      </p>
                      {!notification.read && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 ml-auto flex-shrink-0">
                          {t('notifications.unread', { defaultValue: 'Unread' })}
                        </span>
                      )}
                      {notification.read && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 ml-auto flex-shrink-0">
                          {t('notifications.read', { defaultValue: 'Read' })}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDistanceToNow(new Date(getCreatedAtMillis(notification.createdAt)), { addSuffix: true })}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteNotification(notification.id)
                    }}
                    className="flex-shrink-0 text-gray-400 hover:text-red-500 transition"
                    title={t('common.delete', { defaultValue: 'Delete' })}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-12">
              <BellIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">{t('notifications.noNotifications')}</p>
              <p className="text-xs text-gray-400 mt-1">UID: {getCurrentUid() || 'not-authenticated'}</p>
              <p className="text-sm text-gray-400 mt-1">{t('notifications.youWillSeeHere')}</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default Notifications